import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useSendMessage } from "@/components/AI/chat/useSendMessage";
import {
  AI_AGENT,
  AI_MODEL,
  useCurrentConvo,
} from "@/store/zustand/aiActionStore";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useCanvasId, getRelativePathFromCanvas } from "@/utils/utils";

interface GenerateStoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  componentName?: string;
  componentPath?: string;
  hasExistingStories?: boolean;
  instanceUsageContext?: any; // Static analysis from extractedComponentFile
  componentInstance?: any; // The actual component instance element
}

export const GenerateStoriesModal: React.FC<GenerateStoriesModalProps> = ({
  isOpen,
  onClose,
  componentName,
  componentPath,
  hasExistingStories = false,
  instanceUsageContext,
  componentInstance,
}) => {
  const [context, setContext] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { sendMessage } = useSendMessage();
  const { setNewLayout, expandLeft } = useLayoutStore();
  const { messages: messagesFromConvo } = useCurrentConvo();
  const canvasId = useCanvasId();
  const setIsGeneratingStories = useCanvasStore(
    (state) => state.setIsGeneratingStories,
  );

  useEffect(() => {
    return () => {
      setIsCreating(false);
    };
  }, []);

  const [error, setError] = useState<string | null>(null);

  const buildComponentInfo = (name?: string, path?: string) => {
    if (name && path) return `${name} component (${path})`;
    if (name) return `${name} component`;
    if (path) return `component at ${path}`;
    return "component";
  };

  const buildContextString = () => {
    let contextString = context || "";

    // Add instance-specific usage context if available
    if (instanceUsageContext && componentInstance) {
      const usageAnalysis = `
Component Instance Usage Analysis:
- This story MUST focus on how the component is specifically used in this context
- Instance location: ${componentInstance.filename || "unknown file"}
- Static analysis data: ${JSON.stringify(instanceUsageContext, null, 2)}`;

      contextString = contextString
        ? `${contextString}\n${usageAnalysis}`
        : usageAnalysis;
    }

    return contextString;
  };

  const buildUserPrompt = () => {
    const relativePath = getRelativePathFromCanvas(
      componentPath || "",
      canvasId,
    );
    const componentInfo = buildComponentInfo(componentName, relativePath);

    const basePrompt = hasExistingStories
      ? `Generate additional storybook story(s) for the ${componentInfo} based on the provided context.`
      : `Generate high value specifications for generating storybook stories for the ${componentInfo} based on how the component is used on the codebase.`;

    return basePrompt;
  };

  const buildAdditionalInstructions = () => {
    const contextString = buildContextString();

    // Base instruction based on existing stories and instance usage context
    let baseInstruction;
    if (instanceUsageContext) {
      baseInstruction =
        "Create ONLY 1 story that reflects the specific component instance usage provided in the context.";
    } else if (hasExistingStories) {
      baseInstruction =
        "Create additional story based on the provided context if the component is storyable.";
    } else {
      baseInstruction =
        "Generate and create AT MOST 4 stories covering the main use cases and variations.";
    }

    return `
${contextString ? `Additional context: ${contextString}` : ""}

${baseInstruction} 

Summarize the story specs in a human readable format and then proceed to story generation.
`;
  };

  const handleCreate = async () => {
    if (isCreating) return;

    setIsCreating(true);
    setError(null);

    try {
      const prompt = buildUserPrompt();
      const additionalInstructions = buildAdditionalInstructions();

      if (!sendMessage) {
        throw new Error("AI chat integration is not available");
      }

      // Switch to AI Chat tab and expand left panel
      setNewLayout(LeftTab.AIChat);
      expandLeft();

      // Close the modal first
      onClose();

      const messagesCanSummarize = [...(messagesFromConvo || [])];
      const messagesDoNotSummarize = [];

      await sendMessage({
        userPrompt: prompt,
        additionalInstructions,
        messagesCanSummarize,
        messagesDoNotSummarize,
        agentChoice: AI_AGENT.MAX_AGENT,
        chatOnlyMode: false,
        useReasoning: false,
        useSearch: false,
        isFreePrompt: false,
        codeGenerationModel: AI_MODEL.CLAUDE_4_6_SONNET,
      });

      // Story generation completed successfully
    } catch (error) {
      console.error("Error generating stories:", error);
      setError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while generating stories",
      );
      // Keep the modal open if there's an error
      setIsCreating(false);
      return;
    } finally {
      // Always clear loading state regardless of success or failure
      setIsGeneratingStories(false);
    }

    setIsCreating(false);
  };

  const handleCancel = () => {
    setContext("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-[500px] bg-secondary-100 border-secondary-400 p-4 gap-2">
        <DialogHeader>
          <DialogTitle className="text-base font-normal text-white">
            {hasExistingStories ? "New Story" : "Generate Stories"}
          </DialogTitle>
          <DialogDescription className="text-secondary-n-400 text-xs">
            {hasExistingStories
              ? "Generate additional Storybook stories to complement existing ones. Provide additional context if needed."
              : "Generate Storybook stories for your component. Provide additional context if needed."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-4">
          <div>
            <textarea
              className="w-full mt-1 p-2 rounded-md bg-secondary-200 border border-secondary-400 text-white text-xs placeholder-secondary-600 focus:outline-hidden focus:border-secondary-500 resize-none"
              placeholder="Add additional context for AI (optional)"
              rows={3}
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </div>

          {error && (
            <div className="text-red-400 text-xs bg-red-900/20 border border-red-800 rounded-md p-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-white hover:bg-secondary-300 text-xs rounded-md"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreate}
              disabled={isCreating}
              className="text-xs px-6 rounded-md"
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
